import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from './Button';
import { Card } from './Card';

type Props = {
  children: ReactNode;
};

type State = {
  error: Error | null;
};

/**
 * Last-resort root boundary. If anything in the React tree throws during
 * render, we paint a calm fallback with a Reset action that wipes the
 * persisted store and reloads. The fallback uses native CSS variables
 * (theme tokens) so it stays themed even if the rest of the design
 * system is the source of the crash.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in dev. In production a logger/Sentry hook would go here.
    console.error('Margin crashed:', error, info.componentStack);
  }

  private handleReset = () => {
    try {
      // Wipe the persisted store key — anything else is implementation
      // detail of the storage layer.
      localStorage.removeItem('margin:appdata:v1');
    } catch {
      /* ignore */
    }
    if (typeof window !== 'undefined') window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.error) return this.props.children;
    return (
      <main className="min-h-full bg-bg flex items-center justify-center px-screen-x">
        <div className="max-w-content w-full">
          <Card elevation="lg" className="p-card-pad">
            <div className="flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-card bg-negative/10 inline-flex items-center justify-center text-negative">
                <AlertCircle size={24} strokeWidth={1.5} />
              </div>
              <h1 className="text-section text-text-primary">
                Something went sideways
              </h1>
              <p className="text-[14px] text-text-secondary max-w-[320px]">
                Margin hit an unexpected error. Reloading usually fixes it.
                If it keeps happening, resetting your local data will start
                you over.
              </p>
              <pre className="text-[11px] text-text-tertiary font-mono whitespace-pre-wrap mt-2 max-h-24 overflow-y-auto">
                {this.state.error.message}
              </pre>
              <div className="grid grid-cols-2 gap-2 w-full mt-2">
                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={() => window.location.reload()}
                >
                  Reload
                </Button>
                <Button
                  variant="destructive"
                  size="lg"
                  fullWidth
                  leadingIcon={<RotateCcw size={16} strokeWidth={1.5} />}
                  onClick={this.handleReset}
                >
                  Reset data
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </main>
    );
  }
}
